import { ReactNode } from 'react'

type TextVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'body' | 'body-sm' | 'caption' | 'label' | 'overline'

interface TextProps {
    variant?: TextVariant
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label' | 'div'
    children: ReactNode
    muted?: boolean
    className?: string
    htmlFor?: string
}

const variantClasses: Record<TextVariant, string> = {
    h1: 'text-2xl font-semibold text-slate-900 dark:text-slate-100',
    h2: 'text-xl font-semibold text-slate-900 dark:text-slate-100',
    h3: 'text-base font-semibold text-slate-900 dark:text-slate-100',
    h4: 'text-sm font-semibold text-slate-900 dark:text-slate-100',
    body: 'text-sm text-slate-700 dark:text-slate-300',
    'body-sm': 'text-xs text-slate-600 dark:text-slate-400',
    caption: 'text-xs text-slate-500 dark:text-slate-400',
    label: 'text-sm font-medium text-slate-900 dark:text-slate-100',
    overline: 'text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400',
}

const defaultTags: Record<TextVariant, string> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    body: 'p',
    'body-sm': 'p',
    caption: 'p',
    label: 'label',
    overline: 'span',
}

export const Text = ({ variant = 'body', as, muted, children, className = '', htmlFor }: TextProps) => {
    const tag = as || defaultTags[variant]
    const mutedClass = muted ? 'text-slate-500 dark:text-slate-400' : ''
    const combinedClass = `${variantClasses[variant]} ${mutedClass} ${className}`.trim()

    switch (tag) {
        case 'h1':
            return <h1 className={combinedClass}>{children}</h1>
        case 'h2':
            return <h2 className={combinedClass}>{children}</h2>
        case 'h3':
            return <h3 className={combinedClass}>{children}</h3>
        case 'h4':
            return <h4 className={combinedClass}>{children}</h4>
        case 'h5':
            return <h5 className={combinedClass}>{children}</h5>
        case 'h6':
            return <h6 className={combinedClass}>{children}</h6>
        case 'label':
            return (
                <label className={combinedClass} htmlFor={htmlFor}>
                    {children}
                </label>
            )
        case 'span':
            return <span className={combinedClass}>{children}</span>
        case 'div':
            return <div className={combinedClass}>{children}</div>
        default:
            return <p className={combinedClass}>{children}</p>
    }
}
